import { useCallback, useMemo, useState } from 'react';
import { i18n } from '@lingui/core';
import { Trans, useLingui } from '@lingui/react';
import {
  Button,
  Card,
  DataGrid,
  Icon,
  Panel,
  Popup,
  Text,
  LineChart,
  colors,
} from '@mindbaz/ui-v2';
import { useQuery } from '@tanstack/react-query';
import { format, parse } from 'date-fns';
import { uniqueId } from 'lodash';

import TitleDescriptionBlock from 'components/common/TitleDescriptionBlock';
import {
  getPerson,
  getUserInformation,
  isAdmin,
} from 'packages/endpoints/common';
import { getDatabasestats, getdailyStats } from 'packages/endpoints/safeSend';
import { PERIOD_MESSAGES } from 'packages/locales/i18n';

import { TCard, TDatabaseStat } from './types';
import useDataGrid from './useDataGrid';
import { PERIOD_PANEL, periodOptions, TPeriodPanel } from './utils';
import {
  StyledCards,
  StyledButton,
  StyledRadioButton,
  ChartContainer,
  PopupContainer,
  Flex,
  Link,
} from './styledComponents';

const SafeSend = () => {
  const { _ } = useLingui();
  const [isOpenModal, setOpenModal] = useState(false);

  const { data: isAdminUser } = useQuery(['is-admin'], () => isAdmin());

  const { data: userInformations } = useQuery(['get-user-information'], () =>
    getUserInformation()
  );

  const { data: person } = useQuery(['get-person'], () => getPerson());

  const adminListOfCurrentUser = userInformations.adminList.split(';');

  const periodsPanel = Object.values(PERIOD_PANEL);

  const [isOpenPanel, setIsOpenPanel] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<TPeriodPanel>(
    PERIOD_PANEL.THIRTY_LAST_DAYS
  );

  const { columns } = useDataGrid();

  const formatDateStandard = (date: Date) => format(date, 'dd/MM/yyyy');

  const formatDateISO = (date: Date) => {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
  };

  const datesOfSelectedPeriodFormatISO = useMemo(() => {
    const { startDate, endDate } = periodOptions[selectedPeriod];
    const parsedStartDate = parse(startDate, 'dd/MM/yyyy', new Date());
    const parsedEndDate = parse(endDate, 'dd/MM/yyyy', new Date());
    const start = formatDateISO(parsedStartDate);
    const end = formatDateISO(parsedEndDate);

    return { start, end };
  }, [selectedPeriod]);

  const handleSlectedPeriod = useCallback((period: TPeriodPanel) => {
    setSelectedPeriod(period);
  }, []);

  const { data: databaseStats } = useQuery(
    ['database-stats', selectedPeriod],
    () =>
      person &&
      periodOptions[selectedPeriod] &&
      getDatabasestats(
        person.idCompany,
        datesOfSelectedPeriodFormatISO.start,
        datesOfSelectedPeriodFormatISO.end
      ),
    {
      suspense: false,
    }
  );

  const { data: dailyStats } = useQuery(
    ['daily-stats', selectedPeriod],
    () =>
      person &&
      periodOptions[selectedPeriod] &&
      getdailyStats(
        person.idCompany,
        datesOfSelectedPeriodFormatISO.start,
        datesOfSelectedPeriodFormatISO.end
      ),
    {
      suspense: false,
    }
  );

  const dailyStatsFormattedData = useMemo(() => {
    if (dailyStats && dailyStats.result.length > 0) {
      const blackListedEmails = dailyStats.result.map(
        (email: { day: string; nbBlackListedEmails: number }) => ({
          x: email.day,
          y: email.nbBlackListedEmails,
        })
      );
      const suspiciousActions = dailyStats.result.map(
        (email: { day: string; nbSuspiciousActions: number }) => ({
          x: email.day,
          y: email.nbSuspiciousActions,
        })
      );
      return [
        {
          id: i18n._('safesend.dailyStats.excludedEmails'),
          data: blackListedEmails,
        },
        {
          id: i18n._('safesend.dailyStats.suspiciousActions'),
          data: suspiciousActions,
        },
      ];
    }
    return [];
  }, [dailyStats]);

  const formatEmailsAndActionsByDB = useMemo(() => {
    return (
      databaseStats &&
      databaseStats.result.map((data: TDatabaseStat) => {
        const { currentSubState, stateUntilOrFromDate } = data;

        if (stateUntilOrFromDate === '0001-01-01T00:00:00') {
          return { ...data, currentSubState, id: uniqueId() };
        }
        if (currentSubState === 'deactivated from') {
          return {
            id: uniqueId(),
            nbBlackListedEmails: '-',
            nbSuspiciousActions: '-',
            currentSubState: i18n._('safesend.databaseStats.fromDate', {
              date: formatDateStandard(new Date(stateUntilOrFromDate)),
            }),
          };
        }
        if (currentSubState === 'activated until') {
          return {
            ...data,
            id: uniqueId(),
            currentSubState: i18n._('safesend.databaseStats.untilDate', {
              date: formatDateStandard(new Date(stateUntilOrFromDate)),
            }),
          };
        }
        if (currentSubState === 'activated from') {
          return {
            ...data,
            id: uniqueId(),
            currentSubState: i18n._('safesend.databaseStats.fromDate', {
              date: formatDateStandard(new Date(stateUntilOrFromDate)),
            }),
          };
        }
        return { ...data, currentSubState, id: uniqueId() };
      })
    );
  }, [databaseStats]);

  const formatDateText = () => {
    const { startDate, endDate } = periodOptions[selectedPeriod];
    return i18n._('safesend.databaseStats.dateRange', {
      startDate,
      endDate,
    });
  };

  const cards = [
    {
      title: i18n._('safesend.cards.emails.title'),
      description: i18n._('safesend.cards.emails.description', {
        data: databaseStats && databaseStats.totalBlacklistedEmails,
      }),
      icon: 'block',
      data:
        databaseStats &&
        databaseStats.totalBlacklistedEmails.toLocaleString('FR-fr'),
    },
    {
      title: i18n._('safesend.cards.actions.title'),
      description: i18n._('safesend.cards.actions.description', {
        data: databaseStats && databaseStats.totalSuspiciousActions,
      }),
      icon: 'smart_toy',
      data:
        databaseStats &&
        databaseStats.totalSuspiciousActions.toLocaleString('FR-fr'),
    },
  ];

  const handleOpenPanel = () => {
    setIsOpenPanel(!isOpenPanel);
  };

  const handleRedirection = () => {
    if (isAdminUser)
      window.location.replace(`${import.meta.env.VITE_ADMIN_URL}safesend`);
    else {
      setOpenModal(!isOpenModal);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const chartColors = [colors.Bleu700, colors.Bleu400];

  const translations = useMemo(
    () => ({
      search: i18n._('table.search'),
      elementsTabCount: i18n._('table.elementsTabCount'),
    }),
    []
  );

  return (
    <>
      <Flex className="justify-between">
        <TitleDescriptionBlock
          title={i18n._("safesend.title")}
          description={i18n._("safesend.description")}
          moreDescription={
            <Link href={i18n._('safesend.descriptionLink')}>
              <Trans id="safesend.descriptionClickHere" message="click here." />
            </Link>
          }
        />
        <StyledButton onClick={handleOpenPanel} endIcon="event">
          {formatDateText()}
        </StyledButton>
        <Panel
          title={i18n._('safesend.panel.title')}
          description={i18n._('safesend.panel.period')}
          open={isOpenPanel}
          onClose={handleOpenPanel}
        >
          {periodsPanel.map((period) => (
            <div key={period}>
              <StyledRadioButton
                type="radio"
                id={period}
                value={_(PERIOD_MESSAGES[period])}
                checked={selectedPeriod === period}
                onChange={() => {
                  handleSlectedPeriod(period);
                }}
              />
              <label htmlFor={period}>{_(PERIOD_MESSAGES[period])}</label>
            </div>
          ))}
        </Panel>
      </Flex>
      <Flex className="mb-10">
        <StyledCards>
          {cards.map(({ title, description, icon, data }: TCard) => (
            <Card key={title} title={title} footer={description}>
              <Flex className="justify-between">
                <Text variant="h1" weight="bold">{data}</Text>
                <Icon color="secondary" size={30}>
                  {icon}
                </Icon>
              </Flex>
            </Card>
          ))}
        </StyledCards>
      </Flex>
      {dailyStatsFormattedData && dailyStatsFormattedData.length > 0 && (
        <ChartContainer>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <LineChart
            data={dailyStatsFormattedData}
            colors={chartColors}
            height={250}
            margin={{ left: 50, bottom: 25, top: 20, right: 20 }}
            xScale={{
              type: 'time',
              format: '%Y-%m-%dT%H:%M:%S',
              precision: 'day',
              useUTC: false,
            }}
            axisXFormat="%d/%m"
            axisBottom={{
              format: '%d/%m',
              tickValues:
                (selectedPeriod === PERIOD_PANEL.LAST_MONTH ||
                  selectedPeriod === PERIOD_PANEL.CURRENT_MONTH ||
                  selectedPeriod === PERIOD_PANEL.THIRTY_LAST_DAYS) &&
                dailyStats?.result?.length > 15
                  ? 31
                  : 15,
            }}
            enableSlices="x"
          />
        </ChartContainer>
      )}
      <Flex className="justify-between mb-20 mt-50">
        <Flex className="flex-column">
          <Text variant="h3" weight="bold">
            <Trans
              id="safesend.databaseDetails"
              message="Details by database"
            />
          </Text>
          <Text variant="body">{formatDateText()}</Text>
        </Flex>
        <Button
          variant="primary"
          variantColor="DarkPink"
          onClick={handleRedirection}
        >
          <Trans id="safesend.updateOffer" message="Modify my SafeSend offer" />
        </Button>
        <Popup
          onClose={handleCloseModal}
          title={i18n._('safesend.popup.title')}
          open={isOpenModal}
        >
          <PopupContainer>
            <Text>
              {i18n._('safesend.popup.description', {
                adminList: adminListOfCurrentUser,
              })}
            </Text>
            <Button onClick={handleCloseModal}>
              <Trans
                id="safesend.popup.confirm"
                message="Modify my SafeSend offer"
              />
            </Button>
          </PopupContainer>
        </Popup>
      </Flex>
      <Flex className="pb-4">
        {formatEmailsAndActionsByDB && (
          <DataGrid
            maximumRowActionsDisplayed={3}
            checkboxSelection={false}
            columns={columns}
            rows={formatEmailsAndActionsByDB}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 30, 50, 100]}
            disableFilters
            translations={translations}
          />
        )}
      </Flex>
    </>
  );
};
export default SafeSend;
